/* eslint-disable react/prop-types */
import { CheckCircle2, ChevronLeft, Clipboard, Code2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/shared/components/ui/button';

export function methodClass(method) {
  if (method === 'GET') return 'border-sky-200 bg-sky-50 text-sky-700';
  if (method === 'POST') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (method === 'PUT') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (method === 'DELETE') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

export function slugForCodeTitle(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function CodeBlock({ title, code }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950 shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-4 py-2">
        <div className="flex items-center gap-2 text-xs font-medium text-slate-300">
          <Code2 className="h-4 w-4 text-cyan-300" />
          {title}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-slate-400 hover:bg-slate-800 hover:text-white"
          onClick={handleCopy}
          aria-label={`Copy ${title}`}
        >
          <Clipboard className="h-4 w-4" />
        </Button>
      </div>
      <pre className="max-h-[520px] overflow-auto p-4 text-xs leading-6 text-slate-100 sm:text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function MethodBadge({ method }) {
  return (
    <span className={`rounded border px-2 py-1 text-xs font-bold ${methodClass(method)}`}>
      {method}
    </span>
  );
}

export function SectionHeader({ icon: Icon, eyebrow, title, description }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">{eyebrow}</p>}
        <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
        {description && <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>}
      </div>
    </div>
  );
}

export function FieldsTable({ title, rows }) {
  return (
    <div className="rounded-xl border border-slate-200">
      <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
        {title}
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map(([name, type, description]) => {
          const isRequired = /^Required\b/.test(description);
          const cleanDesc = isRequired ? description.replace(/^Required\.?\s*/, '') : description;
          return (
            <div key={`${title}-${name}`} className="grid gap-1 px-3 py-3 text-sm sm:grid-cols-[160px_120px_minmax(0,1fr)]">
              <code className="text-xs font-semibold text-slate-900">
                {name}{isRequired && <span className="text-red-500"> *</span>}
              </code>
              <span className="text-xs text-slate-500">{type}</span>
              <p className="text-xs leading-5 text-slate-600">{cleanDesc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function InvariantCard({ endpoint }) {
  return (
    <div className="grid gap-2 rounded-xl border border-cyan-100 bg-cyan-50 p-3 text-xs text-cyan-950 sm:grid-cols-3">
      <div>
        <span className="font-semibold">Surface:</span> {endpoint.surface}
      </div>
      <div>
        <span className="font-semibold">Auth:</span> {endpoint.auth}
      </div>
      <div>
        <span className="font-semibold">Version:</span> {endpoint.version}
      </div>
    </div>
  );
}

export function EndpointDetails({ endpoint, example, onBack }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mb-4 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950"
        >
          <ChevronLeft className="h-4 w-4" />
          {endpoint.group}
        </button>
      )}
      <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <MethodBadge method={endpoint.method} />
            <code className="rounded-md bg-slate-100 px-2 py-1 text-sm text-slate-900">
              {endpoint.path}
            </code>
          </div>
          <h2 className="text-xl font-semibold text-slate-950">{endpoint.title}</h2>
          <p className="mt-1 text-sm leading-6 text-slate-600">{endpoint.description}</p>
        </div>
      </div>

      <InvariantCard endpoint={endpoint} />

      <div className="mt-5 grid gap-4">
        <FieldsTable title="Request" rows={endpoint.requestFields} />
        <FieldsTable title="Response" rows={endpoint.responseFields} />
      </div>

      {endpoint.notes?.length > 0 && (
        <div className="mt-4 rounded-xl border border-cyan-100 bg-cyan-50 p-4">
          <h3 className="text-sm font-semibold text-cyan-950">Notes</h3>
          <ul className="mt-2 space-y-2 text-sm leading-6 text-cyan-900">
            {endpoint.notes.map((note) => (
              <li key={note} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <CodeBlock title={`${slugForCodeTitle(endpoint.title)}.sh`} code={example.request} />
        <CodeBlock title="response.json" code={example.response} />
      </div>
    </section>
  );
}

export function EndpointList({ endpoints, onSelectEndpoint }) {
  return (
    <div className="grid gap-3">
      {endpoints.map((endpoint) => (
        <button
          key={endpoint.id}
          type="button"
          onClick={() => onSelectEndpoint(endpoint.id)}
          className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-cyan-300 hover:bg-cyan-50"
        >
          <div className="flex flex-wrap items-center gap-2">
            <MethodBadge method={endpoint.method} />
            <code className="text-sm text-slate-950">{endpoint.path}</code>
          </div>
          <p className="mt-2 text-sm font-semibold text-slate-950">{endpoint.title}</p>
          <p className="mt-1 text-xs leading-5 text-slate-600">{endpoint.description}</p>
        </button>
      ))}
    </div>
  );
}
